import CloseIcon from '@mui/icons-material/Close';
import { FC, ReactNode } from 'react';

type LeadConfirmModalProps = {
  isOpen: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  confirmTone?: 'danger' | 'primary';
  submitting?: boolean;
  confirmIcon?: ReactNode;
  onClose: () => void;
  onConfirm: () => void;
};

/** Generic confirm/cancel modal for Leads CRM destructive or high-impact actions. */
export const LeadConfirmModal: FC<LeadConfirmModalProps> = ({
  isOpen,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  confirmTone = 'primary',
  submitting = false,
  confirmIcon,
  onClose,
  onConfirm,
}) => {
  if (!isOpen) return null;

  const confirmClass =
    confirmTone === 'danger'
      ? 'bg-[#DC2626] hover:bg-[#B91C1C]'
      : 'bg-[#013847] hover:bg-[#012A35]';

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <button
        type="button"
        aria-label="Close confirmation"
        className="absolute inset-0 bg-[#013847]/40 backdrop-blur-[1px]"
        onClick={() => !submitting && onClose()}
      />
      <dialog
        open
        aria-labelledby="lead-confirm-modal-title"
        className="relative z-10 w-full max-w-md overflow-hidden rounded-2xl border border-[#E9E9E9] bg-white shadow-[0_16px_48px_rgba(1,56,71,0.18)]"
      >
        <div className="border-b border-[#E9E9E9] bg-gradient-to-r from-[#EFF8FA] to-white px-6 py-5">
          <div className="flex items-start justify-between gap-4">
            <h2 id="lead-confirm-modal-title" className="text-lg font-bold text-[#013847] font-raleway">
              {title}
            </h2>
            <button
              type="button"
              aria-label="Close"
              disabled={submitting}
              className="rounded-lg p-1 text-[#737373] hover:bg-[#F5F5F5] disabled:opacity-50"
              onClick={onClose}
            >
              <CloseIcon fontSize="small" />
            </button>
          </div>
        </div>

        <div className="px-6 py-5 font-open-sans text-sm text-[#2C3E50]">
          <p>{message}</p>
        </div>

        <div className="flex flex-wrap gap-3 border-t border-[#E9E9E9] bg-[#FAFAFA] px-6 py-4">
          <button
            type="button"
            disabled={submitting}
            className="flex-1 rounded-xl border border-[#E9E9E9] bg-white px-4 py-2.5 text-sm font-semibold text-[#013847] font-raleway disabled:opacity-60"
            onClick={onClose}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            disabled={submitting}
            className={`inline-flex flex-1 items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold text-white font-raleway disabled:opacity-60 ${confirmClass}`}
            onClick={onConfirm}
          >
            {confirmIcon}
            {submitting ? 'Working…' : confirmLabel}
          </button>
        </div>
      </dialog>
    </div>
  );
};
